// Translation catalogs, bundled per locale + namespace.
//
// Catalogs are small and the language only changes on a full page load, so
// everything is bundled statically (no async loading, SSR-safe).
//
// Adding a namespace: create locales/<code>/<ns>.json for every locale, then
// add it here. Nothing else changes.

import enCard from './locales/en-US/card.json'
import enComplete from './locales/en-US/complete.json'
import enCommon from './locales/en-US/common.json'
import enValidation from './locales/en-US/validation.json'
import esCard from './locales/es-LA/card.json'
import esComplete from './locales/es-LA/complete.json'
import esCommon from './locales/es-LA/common.json'
import esValidation from './locales/es-LA/validation.json'
import enChecklist from './locales/en-US/checklist.json'
import esChecklist from './locales/es-LA/checklist.json'
import enCustomJson from './locales/en-US/customJson.json'
import esCustomJson from './locales/es-LA/customJson.json'
import enDragAndDrop from './locales/en-US/dragAndDrop.json'
import esDragAndDrop from './locales/es-LA/dragAndDrop.json'
import enFiltering from './locales/en-US/filtering.json'
import esFiltering from './locales/es-LA/filtering.json'
import enForm from './locales/en-US/form.json'
import esForm from './locales/es-LA/form.json'
import enGallery from './locales/en-US/gallery.json'
import esGallery from './locales/es-LA/gallery.json'
import enGrocery from './locales/en-US/grocery.json'
import esGrocery from './locales/es-LA/grocery.json'
import enImageEnhancement from './locales/en-US/imageEnhancement.json'
import esImageEnhancement from './locales/es-LA/imageEnhancement.json'
import enIncommOffer from './locales/en-US/incommOffer.json'
import esIncommOffer from './locales/es-LA/incommOffer.json'
import enLanding from './locales/en-US/landing.json'
import esLanding from './locales/es-LA/landing.json'
import enLeadGen from './locales/en-US/leadGen.json'
import esLeadGen from './locales/es-LA/leadGen.json'
import enListicle from './locales/en-US/listicle.json'
import esListicle from './locales/es-LA/listicle.json'
import enLocator from './locales/en-US/locator.json'
import esLocator from './locales/es-LA/locator.json'
import enMemoryMatch from './locales/en-US/memoryMatch.json'
import esMemoryMatch from './locales/es-LA/memoryMatch.json'
import enPersonalityQuiz from './locales/en-US/personalityQuiz.json'
import esPersonalityQuiz from './locales/es-LA/personalityQuiz.json'
import enPoll from './locales/en-US/poll.json'
import esPoll from './locales/es-LA/poll.json'
import enPrivacyRights from './locales/en-US/privacyRights.json'
import esPrivacyRights from './locales/es-LA/privacyRights.json'
import enPromo from './locales/en-US/promo.json'
import esPromo from './locales/es-LA/promo.json'
import enProductScan from './locales/en-US/productScan.json'
import esProductScan from './locales/es-LA/productScan.json'
import enPuzzle from './locales/en-US/puzzle.json'
import esPuzzle from './locales/es-LA/puzzle.json'
import enReceipt from './locales/en-US/receipt.json'
import esReceipt from './locales/es-LA/receipt.json'
import enReferral from './locales/en-US/referral.json'
import esReferral from './locales/es-LA/referral.json'
import enReview from './locales/en-US/review.json'
import esReview from './locales/es-LA/review.json'
import enSampleStep from './locales/en-US/sampleStep.json'
import esSampleStep from './locales/es-LA/sampleStep.json'
import enShared from './locales/en-US/shared.json'
import enSiteChrome from './locales/en-US/siteChrome.json'
import esShared from './locales/es-LA/shared.json'
import esSiteChrome from './locales/es-LA/siteChrome.json'
import enSideBySideVideo from './locales/en-US/sideBySideVideo.json'
import esSideBySideVideo from './locales/es-LA/sideBySideVideo.json'
import enSpotTheDiff from './locales/en-US/spotTheDiff.json'
import esSpotTheDiff from './locales/es-LA/spotTheDiff.json'
import enStepShell from './locales/en-US/stepShell.json'
import esStepShell from './locales/es-LA/stepShell.json'
import enTextTrivia from './locales/en-US/textTrivia.json'
import esTextTrivia from './locales/es-LA/textTrivia.json'
import enTipsAndTricks from './locales/en-US/tipsAndTricks.json'
import esTipsAndTricks from './locales/es-LA/tipsAndTricks.json'
import enUgcMediaUpload from './locales/en-US/ugcMediaUpload.json'
import esUgcMediaUpload from './locales/es-LA/ugcMediaUpload.json'
import enUgcPhotoUpload from './locales/en-US/ugcPhotoUpload.json'
import esUgcPhotoUpload from './locales/es-LA/ugcPhotoUpload.json'

import type { LocaleCode } from './locales'

export const DEFAULT_NS = 'common'

/**
 * Recursively requires the same shape as the reference catalog, with string
 * leaves. A locale missing a namespace — or a key inside one — is a COMPILE
 * ERROR, so a language can never ship half-translated.
 *
 * Note: extra keys are not rejected by structural typing; the runtime parity
 * check covers those. Also assumes locales share plural categories (en/es both
 * use _one/_other); a language with different plural rules would need this
 * relaxed for `_`-suffixed keys.
 */
type Catalog<T> = {
  [K in keyof T]: T[K] extends string ? string : Catalog<T[K]>
}

// en-US is the reference: its catalogs define the required key shape.
const enUS = {
  common: enCommon,
  complete: enComplete,
  card: enCard,
  validation: enValidation,
  checklist: enChecklist,
  customJson: enCustomJson,
  dragAndDrop: enDragAndDrop,
  filtering: enFiltering,
  form: enForm,
  gallery: enGallery,
  grocery: enGrocery,
  imageEnhancement: enImageEnhancement,
  incommOffer: enIncommOffer,
  landing: enLanding,
  leadGen: enLeadGen,
  listicle: enListicle,
  locator: enLocator,
  memoryMatch: enMemoryMatch,
  personalityQuiz: enPersonalityQuiz,
  poll: enPoll,
  // Copy for the privacy rights request page, rendered by the Puck block of the same
  // name. Legal text: keys are per paragraph, never per sentence, so a translator sees
  // the whole clause.
  privacyRights: enPrivacyRights,
  promo: enPromo,
  productScan: enProductScan,
  puzzle: enPuzzle,
  receipt: enReceipt,
  referral: enReferral,
  review: enReview,
  sampleStep: enSampleStep,
  shared: enShared,
  // Header, footer and other site-level chrome. Not a step: these strings belong to the
  // site around the content rather than to any one activity inside it.
  siteChrome: enSiteChrome,
  sideBySideVideo: enSideBySideVideo,
  spotTheDiff: enSpotTheDiff,
  stepShell: enStepShell,
  textTrivia: enTextTrivia,
  tipsAndTricks: enTipsAndTricks,
  ugcMediaUpload: enUgcMediaUpload,
  ugcPhotoUpload: enUgcPhotoUpload,
}

/** Shape every other locale must satisfy. */
export type LocaleCatalogs = Catalog<typeof enUS>

const esLA: LocaleCatalogs = {
  common: esCommon,
  complete: esComplete,
  card: esCard,
  validation: esValidation,
  checklist: esChecklist,
  customJson: esCustomJson,
  dragAndDrop: esDragAndDrop,
  filtering: esFiltering,
  form: esForm,
  gallery: esGallery,
  grocery: esGrocery,
  imageEnhancement: esImageEnhancement,
  incommOffer: esIncommOffer,
  landing: esLanding,
  leadGen: esLeadGen,
  listicle: esListicle,
  locator: esLocator,
  memoryMatch: esMemoryMatch,
  personalityQuiz: esPersonalityQuiz,
  poll: esPoll,
  privacyRights: esPrivacyRights,
  promo: esPromo,
  productScan: esProductScan,
  puzzle: esPuzzle,
  receipt: esReceipt,
  referral: esReferral,
  review: esReview,
  sampleStep: esSampleStep,
  shared: esShared,
  siteChrome: esSiteChrome,
  sideBySideVideo: esSideBySideVideo,
  spotTheDiff: esSpotTheDiff,
  stepShell: esStepShell,
  textTrivia: esTextTrivia,
  tipsAndTricks: esTipsAndTricks,
  ugcMediaUpload: esUgcMediaUpload,
  ugcPhotoUpload: esUgcPhotoUpload,
}

// Record<LocaleCode, ...> means adding a locale to LOCALES without catalogs
// here fails to compile.
export const resources: Record<LocaleCode, LocaleCatalogs> = {
  'en-US': enUS,
  'es-LA': esLA,
}

export const NAMESPACES = Object.keys(enUS)
